import React, { useState } from 'react';
import { api, useAuth, useLanguage } from '../context/AppContext';
import { Button } from './ui/button';
import { Bug, X, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Capture recent console logs for bug reports
const recentLogs = [];
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
console.error = (...args) => {
  recentLogs.push({ level: 'error', msg: args.map(String).join(' ').slice(0, 200), t: new Date().toISOString() });
  if (recentLogs.length > 20) recentLogs.shift();
  originalConsoleError.apply(console, args);
};
console.warn = (...args) => {
  recentLogs.push({ level: 'warn', msg: args.map(String).join(' ').slice(0, 200), t: new Date().toISOString() });
  if (recentLogs.length > 20) recentLogs.shift();
  originalConsoleWarn.apply(console, args);
};

const BugReportButton = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user) return null;

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSending(true);
    try {
      const sessionId = localStorage.getItem('coach_session_id') || '';
      await api.post('/bug-reports', {
        description: description.trim(),
        page: window.location.pathname,
        device_info: `${navigator.userAgent} | ${window.innerWidth}x${window.innerHeight}`,
        session_id: sessionId,
        recent_logs: recentLogs.slice(-10)
      });
      setSent(true);
      setDescription('');
      setTimeout(() => { setOpen(false); setSent(false); }, 2500);
    } catch {
      toast.error(language === 'fr' ? "Erreur lors de l'envoi" : 'Error sending report');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed top-3 right-3 z-40 w-8 h-8 rounded-full bg-red-500/60 hover:bg-red-500 text-white flex items-center justify-center shadow-md transition-all hover:scale-110 hidden md:flex"
        data-testid="bug-report-floating-btn"
        title={language === 'fr' ? 'Signaler un bug' : 'Report a bug'}
      >
        <Bug className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-200">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-red-500/10">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-red-400" />
                <span className="font-heading text-sm uppercase tracking-wider">
                  {language === 'fr' ? 'Signaler un bug' : 'Report a bug'}
                </span>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setOpen(false); setSent(false); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {sent ? (
              <div className="p-8 flex flex-col items-center gap-3 text-center" data-testid="bug-report-success">
                <CheckCircle className="w-14 h-14 text-green-500 animate-in zoom-in duration-300" />
                <p className="font-heading text-lg text-green-400">
                  {language === 'fr' ? 'Signalé avec succès a YUMIX' : 'Successfully reported to YUMIX'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Merci pour ton aide !' : 'Thanks for your help!'}
                </p>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr'
                    ? 'Décris le problème que tu rencontres. YUMIX recevra ton rapport immédiatement.'
                    : "Describe the issue you're experiencing. YUMIX will receive your report immediately."}
                </p>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={language === 'fr'
                    ? 'Ex: Le bouton "Analyser" ne répond pas quand je clique dessus...'
                    : 'Ex: The "Analyze" button doesn\'t respond when I click it...'}
                  className="w-full h-28 px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  autoFocus
                  data-testid="bug-report-textarea"
                />
                <div className="text-xs text-muted-foreground/60">
                  {language === 'fr' ? 'Page : ' : 'Page: '}{window.location.pathname}
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!description.trim() || sending}
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                  data-testid="bug-report-submit-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {language === 'fr' ? 'Envoyer le rapport' : 'Send report'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;
